import React, { useState, useEffect, useMemo, useCallback, useContext } from "react"
import { useNavigate } from "react-router-dom"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Button,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Row,
  Table,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import Swal from "sweetalert2"
import { examMasterApi } from "apis/ExamMasterApi"
import { categoryApi } from "apis/CategoryApi"
import { sessionApi } from "apis/SessionApi"
import { SessionContext } from "context/sessionContext"

const MAX_SUB_ASSESSMENTS = 6

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

const makeAssessmentRow = () => ({
  id: uid(),
  name: "",
  description: "",
  minMarks: "",
  maxMarks: "",
  expanded: false,
  subAssessments: [],
  newSubName: "",
})

const makeSubAssessment = (name = "") => ({ id: uid(), name })

const normalizeCategory = cat => ({
  id: cat.id,
  category_name: cat.category_name || cat.name || "Untitled Category",
  evaluation_type: cat.evaluation_type || "Marks Based",
  marking: cat.marking || "Subject Wise",
  classes:
    typeof cat.st_class === "string"
      ? cat.st_class
          .split(",")
          .map(s => s.trim())
          .filter(Boolean)
          .map(name => ({ id: name, class_name: name }))
      : Array.isArray(cat.st_class)
      ? cat.st_class.map(c => ({
          id: c.uid ?? c.id ?? c.name ?? c,
          class_name: c.name || c.class_name || c.group_name || String(c),
        }))
      : [],
  subjects: (cat.subjects || []).map(s => ({
    id: s.id ?? null,
    subject_code: s.subject_code,
    subject_name: s.subject_name,
    topics: Array.isArray(s.topics)
      ? s.topics.map(t => ({
          id: t.id,
          topic_name: t.topic_name,
          sequence_no: t.sequence_no,
        }))
      : [],
  })),
})

const ExamCreate = () => {
  document.title = "Create Exam"
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)

  // Stored from Step 1 API response
  const [examId, setExamId] = useState(null)

  // Maps local row IDs → backend assessment/sub-assessment IDs after Step 2
  // Shape: { [localRowId]: { backendId: number, subs: { [localSubId]: number } } }
  const [assessmentIdMap, setAssessmentIdMap] = useState({})

  const { Session } = useContext(SessionContext)
  const [fallbackSessionId, setFallbackSessionId] = useState(null)
  const activeSessionId = Session || sessionStorage.getItem("SessionId") || fallbackSessionId

  const [assessmentRows, setAssessmentRows] = useState([makeAssessmentRow()])
  const [rowErrors, setRowErrors] = useState({})

  const [selectedCardKeys, setSelectedCardKeys] = useState([])

  // ── Category state with pagination ──
  const [allCategories, setAllCategories] = useState([])
  const [categoriesLoading, setCategoriesLoading] = useState(false)
  const [categoriesLoadingMore, setCategoriesLoadingMore] = useState(false)
  const [categoriesPage, setCategoriesPage] = useState(1)
  const [categoriesTotalPages, setCategoriesTotalPages] = useState(1)

  const [mappingState, setMappingState] = useState({})
  const [openClassDropdownKey, setOpenClassDropdownKey] = useState(null)
  const [openCategoryDropdownKey, setOpenCategoryDropdownKey] = useState(null)

  useEffect(() => {
    const fetchSession = async () => {
      if (Session || sessionStorage.getItem("SessionId")) return
      try {
        const res = await sessionApi.GetSession()
        if (res?.data?.session && Array.isArray(res.data.session)) {
          const active = res.data.session.find(s => s.is_active)
          if (active) setFallbackSessionId(active.id)
          else if (res.data.session.length > 0)
            setFallbackSessionId(res.data.session[0].id)
        }
      } catch (err) {
        console.error("Failed to fetch sessions:", err.message)
      }
    }
    fetchSession()
  }, [Session])

  // ── Fetch categories with pagination support ──
  const fetchCategories = useCallback(async (page = 1) => {
    try {
      if (page === 1) setCategoriesLoading(true)
      else setCategoriesLoadingMore(true)

      const res = await categoryApi.getAllCategory(page, activeSessionId)
      const categories = (res?.data?.categories || []).map(normalizeCategory)
      const totalPages = res?.data?.total_pages ?? 1

      setAllCategories(prev =>
        page === 1 ? categories : [...prev, ...categories]
      )
      setCategoriesTotalPages(totalPages)
      setCategoriesPage(page)
    } catch (error) {
      console.error("Error fetching categories", error)
    } finally {
      setCategoriesLoading(false)
      setCategoriesLoadingMore(false)
    }
  }, [activeSessionId])

  // ── Derived: assessment cards ──
  const assessmentCards = useMemo(() => {
    const cards = []
    assessmentRows.forEach(row => {
      const name = row.name.trim()
      if (!name) return
      const validSubs = row.subAssessments.filter(sub => sub.name.trim())
      if (validSubs.length > 0) {
        validSubs.forEach(sub => {
          cards.push({
            key: `${row.id}:${sub.id}`,
            rowId: row.id,
            subId: sub.id,
            label: sub.name.trim(),
            parentLabel: name,
            isSubAssessment: true,
          })
        })
      } else {
        cards.push({
          key: row.id,
          rowId: row.id,
          subId: null,
          label: name,
          parentLabel: null,
          isSubAssessment: false,
        })
      }
    })
    return cards
  }, [assessmentRows])

  const cardsByKey = useMemo(() => {
    const map = {}
    assessmentCards.forEach(c => {
      map[c.key] = c
    })
    return map
  }, [assessmentCards])

  useEffect(() => {
    const validKeys = new Set(assessmentCards.map(c => c.key))
    setSelectedCardKeys(prev => prev.filter(k => validKeys.has(k)))
    setMappingState(prev => {
      const next = {}
      Object.keys(prev).forEach(k => {
        if (validKeys.has(k)) next[k] = prev[k]
      })
      return next
    })
  }, [assessmentCards])

  // ── Close class dropdown on outside click ──
  useEffect(() => {
    if (!openClassDropdownKey) return
    const handleOutsideClick = e => {
      if (!e.target.closest(".class-multiselect")) {
        setOpenClassDropdownKey(null)
      }
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [openClassDropdownKey])

  // ── Close category dropdown on outside click ──
  useEffect(() => {
    if (!openCategoryDropdownKey) return
    const handleOutsideClick = e => {
      if (!e.target.closest(".class-multiselect")) {
        setOpenCategoryDropdownKey(null)
      }
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [openCategoryDropdownKey])

  const getClassesForCategory = useCallback(
    categoryId => {
      const cat = allCategories.find(c => String(c.id) === String(categoryId))
      return cat ? cat.classes : []
    },
    [allCategories]
  )

  const getSubjectRowsForCategory = useCallback(
    categoryId => {
      const cat = allCategories.find(c => String(c.id) === String(categoryId))
      if (!cat) return []
      const rows = []
      cat.subjects.forEach(sub => {
        const hasTopics = Array.isArray(sub.topics) && sub.topics.length > 0
        if (hasTopics) {
          rows.push({
            rowType: "subject-header",
            subject_id: sub.id,
            subject_code: sub.subject_code,
            subject_name: sub.subject_name,
            topic_id: null,
            topic_name: null,
          })
          sub.topics.forEach(topic => {
            rows.push({
              rowType: "topic",
              subject_id: sub.id,
              subject_code: sub.subject_code,
              subject_name: sub.subject_name,
              topic_id: topic.id,
              topic_name: topic.topic_name,
              checked: false,
              min_mark: "",
              max_mark: "",
            })
          })
        } else {
          rows.push({
            rowType: "subject",
            subject_id: sub.id,
            subject_code: sub.subject_code,
            subject_name: sub.subject_name,
            topic_id: null,
            topic_name: null,
            checked: false,
            min_mark: "",
            max_mark: "",
          })
        }
      })
      return rows
    },
    [allCategories]
  )

  const getMapping = key =>
    mappingState[key] || { categoryId: "", classIds: [], subjects: [] }

  const handleCardToggle = key => {
    setSelectedCardKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    )
  }

  const handleCategoryChange = (key, categoryId) => {
    setMappingState(prev => ({
      ...prev,
      [key]: {
        categoryId,
        classIds: [],
        subjects: categoryId ? getSubjectRowsForCategory(categoryId) : [],
      },
    }))
  }

  const handleClassToggle = (key, classId) => {
    setMappingState(prev => {
      const current = prev[key] || {
        categoryId: "",
        classIds: [],
        subjects: [],
      }
      const classIds = current.classIds.includes(classId)
        ? current.classIds.filter(id => id !== classId)
        : [...current.classIds, classId]
      return { ...prev, [key]: { ...current, classIds } }
    })
  }

  const handleSubjectToggle = (key, subjectIndex) => {
    setMappingState(prev => {
      const current = prev[key]
      if (!current) return prev
      return {
        ...prev,
        [key]: {
          ...current,
          subjects: current.subjects.map((s, i) => {
            if (i !== subjectIndex) return s
            if (s.rowType === "subject-header") return s
            return {
              ...s,
              checked: !s.checked,
              min_mark: s.checked ? "" : s.min_mark,
              max_mark: s.checked ? "" : s.max_mark,
            }
          }),
        },
      }
    })
  }

  const handleSelectAllSubjects = key => {
    setMappingState(prev => {
      const current = prev[key]
      if (!current) return prev
      const selectableRows = current.subjects.filter(
        s => s.rowType !== "subject-header"
      )
      const allChecked =
        selectableRows.length > 0 && selectableRows.every(s => s.checked)
      return {
        ...prev,
        [key]: {
          ...current,
          subjects: current.subjects.map(s => {
            if (s.rowType === "subject-header") return s
            return {
              ...s,
              checked: !allChecked,
              min_mark: allChecked ? "" : s.min_mark,
              max_mark: allChecked ? "" : s.max_mark,
            }
          }),
        },
      }
    })
  }

  const handleMarkChange = (key, subjectIndex, field, value) => {
    setMappingState(prev => {
      const current = prev[key]
      if (!current) return prev
      return {
        ...prev,
        [key]: {
          ...current,
          subjects: current.subjects.map((s, i) =>
            i === subjectIndex
              ? { ...s, [field]: value === "" ? "" : Number(value) }
              : s
          ),
        },
      }
    })
  }

  // ── Step 1 validation schema ──
  const step1ValidationSchema = Yup.object({
    exam_name: Yup.string()
      .required("Please Enter Exam Name")
      .test("is-empty", "Please Enter Exam Name", value => value && value.trim() !== ""),
    description: Yup.string()
      .required("Please Enter Description")
      .test("is-empty", "Please Enter Description", value => value && value.trim() !== ""),
  })

  const validation = useFormik({
    enableReinitialize: false,
    initialValues: { exam_name: "", description: "" },
    validationSchema: step === 1 ? step1ValidationSchema : null,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async values => {
      // ── STEP 1 ──
      if (step === 1) {
        setLoading(true)
        try {
          const step1Payload = {
            session_id: activeSessionId,
            exam_name: values.exam_name.trim(),
            description: values.description.trim(),
          }
          if (examId) step1Payload.exam_id = examId
          const res = await examMasterApi.addExamStepOne(step1Payload)
          const data = res?.data
          if (data?.status && data.status !== 200) {
            Swal.fire({
              text: data.msg || "Failed to save exam.",
              icon: "error",
              title: "Error",
            })
            return
          }
          if (!data?.exam_id) throw new Error("No exam_id returned from server")
          setExamId(data.exam_id)
          setStep(2)
        } catch (err) {
          console.error("Step 1 error:", err)
          Swal.fire({
            text:
              err.response?.data?.msg ||
              "Failed to save exam. Please try again.",
            icon: "error",
            title: "Error",
          })
        } finally {
          setLoading(false)
        }
        return
      }

      // ── STEP 2 ──
      if (step === 2) {
        const errors = {}

        // Check for duplicate main assessment names
        const mainAssessmentNames = []
        assessmentRows.forEach(row => {
          const trimmed = row.name.trim()
          if (!trimmed) {
            errors[row.id] = "Assessment name is required"
          } else {
            mainAssessmentNames.push({ id: row.id, name: trimmed })
          }
        })
        const mainNameCounts = {}
        mainAssessmentNames.forEach(({ name }) => {
          const key = name.toLowerCase()
          mainNameCounts[key] = (mainNameCounts[key] || 0) + 1
        })
        mainAssessmentNames.forEach(({ id, name }) => {
          if (mainNameCounts[name.toLowerCase()] > 1 && !errors[id]) {
            errors[
              id
            ] = `"${name}" is already used — assessment names must be unique`
          }
        })

        assessmentRows.forEach(row => {
          // Min/max marks validation removed

          // Check sub-assessment names: unique within same parent
          const subNames = {}
          row.subAssessments.forEach(sub => {
            const trimmedSub = sub.name.trim()
            if (!trimmedSub) {
              errors[sub.id] = "Sub-assessment name is required"
            } else {
              const key = trimmedSub.toLowerCase()
              subNames[key] = (subNames[key] || 0) + 1
            }
          })
          row.subAssessments.forEach(sub => {
            const trimmedSub = sub.name.trim()
            if (
              trimmedSub &&
              subNames[trimmedSub.toLowerCase()] > 1 &&
              !errors[sub.id]
            ) {
              errors[
                sub.id
              ] = `"${trimmedSub}" is already used within this assessment — sub-assessment names must be unique per assessment`
            }
          })
        })

        if (Object.keys(errors).length > 0) {
          setRowErrors(errors)
          return
        }
        setRowErrors({})

        // Build payload for Step 2
        const assessmentsPayload = assessmentRows.map(row => {
          const existingRow = assessmentIdMap[row.id]

          const entry = {
            assessment_name: row.name.trim(),
            sub_assessments: row.subAssessments
              .filter(sub => sub.name.trim())
              .map(sub => {
                const subEntry = { sub_assessment_name: sub.name.trim() }
                const existingSubId = existingRow?.subs?.[sub.id]
                if (existingSubId) subEntry.sub_assessment_id = existingSubId
                return subEntry
              }),
          }

          if (existingRow?.backendId)
            entry.assessment_id = existingRow.backendId
          if (row.description.trim()) entry.description = row.description.trim()

          // ── UPDATED: both zero → send "" for both; otherwise default min to 0 ──
          const minVal = row.minMarks !== "" ? Number(row.minMarks) : 0
          const maxVal = row.maxMarks !== "" ? Number(row.maxMarks) : 0
          if (minVal === 0 && maxVal === 0) {
            entry.min_mark = ""
            entry.max_mark = ""
          } else {
            entry.min_mark = minVal
            entry.max_mark = maxVal
          }

          return entry
        })

        setLoading(true)
        try {
          const res = await examMasterApi.addExamStepTwo({
            exam_id: examId,
            assessments: assessmentsPayload,
          })
          if (res?.data?.status && res.data.status !== 200) {
            Swal.fire({
              text: res.data.msg || "Failed to save assessments.",
              icon: "error",
              title: "Error",
            })
            return
          }

          // Build local-ID → backend-ID lookup map (zip by index)
          const returnedAssessments = res?.data?.assessments || []
          const idMap = {}
          assessmentRows.forEach((row, rowIdx) => {
            const backendAssessment = returnedAssessments[rowIdx]
            if (!backendAssessment) return
            const subsMap = {}
            row.subAssessments.forEach((sub, subIdx) => {
              const backendSub = (backendAssessment.sub_assessments || [])[
                subIdx
              ]
              if (backendSub?.sub_assessment_id)
                subsMap[sub.id] = backendSub.sub_assessment_id
            })
            idMap[row.id] = {
              backendId: backendAssessment.assessment_id,
              subs: subsMap,
            }
          })
          setAssessmentIdMap(idMap)

          // Reset category list to page 1 fresh on every Step 3 entry
          setAllCategories([])
          setCategoriesPage(1)
          setCategoriesTotalPages(1)
          await fetchCategories(1)
          setStep(3)
        } catch (err) {
          console.error("Step 2 error:", err)
          Swal.fire({
            text:
              err.response?.data?.msg ||
              "Failed to save assessments. Please try again.",
            icon: "error",
            title: "Error",
          })
        } finally {
          setLoading(false)
        }
        return
      }

      // ── STEP 3 ──
      if (step === 3) {
        if (selectedCardKeys.length === 0) {
          Swal.fire({
            text: "Please select at least one Assessment",
            icon: "warning",
            title: "Validation",
          })
          return
        }

        for (const key of selectedCardKeys) {
          const card = cardsByKey[key]
          const mapping = getMapping(key)
          const label = card?.label || "this assessment"

          if (!mapping.categoryId) {
            Swal.fire({
              text: `Please select a Category for "${label}"`,
              icon: "warning",
              title: "Validation",
            })
            return
          }
          if (mapping.classIds.length === 0) {
            Swal.fire({
              text: `Please select at least one Class for "${label}"`,
              icon: "warning",
              title: "Validation",
            })
            return
          }
          const checkedSubjects = mapping.subjects.filter(
            s => s.rowType !== "subject-header" && s.checked
          )
          if (checkedSubjects.length === 0) {
            Swal.fire({
              text: `Please select at least one subject for "${label}"`,
              icon: "warning",
              title: "Validation",
            })
            return
          }

          const category = allCategories.find(
            c => String(c.id) === String(mapping.categoryId)
          )
          const isGradeBased = category?.evaluation_type === "Grade Based"
          if (!isGradeBased) {
            for (const subj of checkedSubjects) {
              const displayName =
                subj.rowType === "topic"
                  ? `${subj.subject_name} → ${subj.topic_name}`
                  : subj.subject_name

              // Min/max marks validation removed
            }
          }
        }

        // Build payload for Step 3
        const mappingsPayload = selectedCardKeys.map(key => {
          const card = cardsByKey[key]
          const mapping = getMapping(key)
          const category = allCategories.find(
            c => String(c.id) === String(mapping.categoryId)
          )
          const isGradeBased = category?.evaluation_type === "Grade Based"
          const isTopicWise = category?.marking === "Subject + Topic Wise"

          const checkedSubjects = mapping.subjects.filter(
            s => s.rowType !== "subject-header" && s.checked
          )

          const rowEntry = assessmentIdMap[card.rowId] || {}
          const categoryClasses = getClassesForCategory(mapping.categoryId)
          const entry = {
            category_id: mapping.categoryId,
            st_class: mapping.classIds.map(id => {
              const cls = categoryClasses.find(c => String(c.id) === String(id))
              return {
                uid: Number(id),
                name: cls ? cls.class_name : String(id),
              }
            }),
            subjects: checkedSubjects.map(s => {
              const subj = {
                subject_code: s.subject_code,
                subject_name: s.subject_name,
              }
              if (s.rowType === "topic" && s.topic_name) {
                subj.topic_name = s.topic_name
              }
              if (!isGradeBased) {
                // ── UPDATED: both zero → send "" for both; otherwise default min to 0 ──
                const minVal =
                  s.min_mark !== "" && s.min_mark !== null
                    ? Number(s.min_mark)
                    : 0
                const maxVal =
                  s.max_mark !== "" && s.max_mark !== null
                    ? Number(s.max_mark)
                    : 0
                if (minVal === 0 && maxVal === 0) {
                  subj.min_mark = ""
                  subj.max_mark = ""
                } else {
                  subj.min_mark = minVal
                  subj.max_mark = maxVal
                }
              }
              return subj
            }),
          }

          if (card.isSubAssessment) {
            const backendSubId = rowEntry.subs?.[card.subId]
            if (backendSubId) entry.sub_assessment_id = backendSubId
          } else {
            const backendId = rowEntry.backendId
            if (backendId) entry.assessment_id = backendId
          }

          return entry
        })

        setLoading(true)
        try {
          const res = await examMasterApi.addExamStepThree({
            mappings: mappingsPayload,
          })
          if (res?.data?.status && res.data.status !== 200) {
            Swal.fire({
              text: res.data.msg || "Failed to save mappings.",
              icon: "error",
              title: "Error",
            })
            return
          }
          Swal.fire({
            text: res.data?.msg || "Exam created successfully",
            icon: "success",
            title: "Success",
          })
          navigate("/exam-master")
        } catch (err) {
          console.error("Step 3 error:", err)
          Swal.fire({
            text:
              err.response?.data?.msg ||
              "Failed to save mappings. Please try again.",
            icon: "error",
            title: "Error",
          })
        } finally {
          setLoading(false)
        }
      }
    },
  })

  // ── Step 2 row handlers ──
  const handleAddRow = () =>
    setAssessmentRows(prev => [...prev, makeAssessmentRow()])

  const handleRowChange = (id, field, value) => {
    setAssessmentRows(prev =>
      prev.map(row => (row.id === id ? { ...row, [field]: value } : row))
    )
    if (rowErrors[id])
      setRowErrors(prev => {
        const next = { ...prev }
        delete next[id]
        return next
      })
  }

  const handleRemoveRow = id => {
    if (assessmentRows.length === 1) return
    setAssessmentRows(prev => prev.filter(row => row.id !== id))
    setRowErrors(prev => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const handleToggleExpand = id => {
    setAssessmentRows(prev =>
      prev.map(row =>
        row.id === id ? { ...row, expanded: !row.expanded } : row
      )
    )
  }

  const handleNewSubNameChange = (rowId, value) => {
    setAssessmentRows(prev =>
      prev.map(row => (row.id === rowId ? { ...row, newSubName: value } : row))
    )
  }

  const handleAddSubAssessment = rowId => {
    setAssessmentRows(prev =>
      prev.map(row => {
        if (row.id !== rowId) return row

        const name = (row.newSubName || "").trim()

        if (!name || row.subAssessments.length >= MAX_SUB_ASSESSMENTS) {
          return row
        }

        const isDuplicate = row.subAssessments.some(
          sub => sub.name.trim().toLowerCase() === name.toLowerCase()
        )

        if (isDuplicate) {
          Swal.fire({
            icon: "error",
            title: "Duplicate Sub-Assessment",
            text: "This sub-assessment already exists in this assessment.",
            confirmButtonText: "OK",
          })
          return row
        }

        return {
          ...row,
          newSubName: "",
          subAssessments: [...row.subAssessments, makeSubAssessment(name)],
        }
      })
    )
  }

  const handleNewSubKeyDown = (rowId, e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddSubAssessment(rowId)
    }
  }

  const handleRemoveSubAssessment = (rowId, subId) => {
    setAssessmentRows(prev =>
      prev.map(row =>
        row.id !== rowId
          ? row
          : {
              ...row,
              subAssessments: row.subAssessments.filter(
                sub => sub.id !== subId
              ),
            }
      )
    )
    setRowErrors(prev => {
      const next = { ...prev }
      delete next[subId]
      return next
    })
  }

  const handleCancel = () => navigate("/exam-master")
  const handlePrevious = () => {
    if (step === 2) setStep(1)
    else if (step === 3) setStep(2)
  }

  // ── Category dropdown infinite scroll handler ──
  const handleCategoryPanelScroll = useCallback(
    e => {
      const el = e.currentTarget
      const nearBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 40
      if (
        nearBottom &&
        !categoriesLoadingMore &&
        categoriesPage < categoriesTotalPages
      ) {
        fetchCategories(categoriesPage + 1)
      }
    },
    [
      categoriesLoadingMore,
      categoriesPage,
      categoriesTotalPages,
      fetchCategories,
    ]
  )

  const Stepper = () => (
    <div className="d-flex align-items-center mb-4">
      <div className="d-flex align-items-center">
        <div
          className={`exam-step-num ${
            step > 1 ? "done" : step === 1 ? "active" : "pending"
          }`}
        >
          {step > 1 ? "✓" : "1"}
        </div>
        <span
          className={`exam-step-label ${
            step > 1 ? "done" : step === 1 ? "active" : "pending"
          }`}
        >
          Create Exam
        </span>
      </div>
      <div className={`exam-step-line ${step > 1 ? "done" : ""}`} />
      <div className="d-flex align-items-center">
        <div
          className={`exam-step-num ${
            step > 2 ? "done" : step === 2 ? "active" : "pending"
          }`}
        >
          {step > 2 ? "✓" : "2"}
        </div>
        <span
          className={`exam-step-label ${
            step > 2 ? "done" : step === 2 ? "active" : "pending"
          }`}
        >
          Assessment
        </span>
      </div>
      <div className={`exam-step-line ${step > 2 ? "done" : ""}`} />
      <div className="d-flex align-items-center">
        <div className={`exam-step-num ${step === 3 ? "active" : "pending"}`}>
          3
        </div>
        <span
          className={`exam-step-label ${step === 3 ? "active" : "pending"}`}
        >
          Subject Mapping
        </span>
      </div>
    </div>
  )

  return (
    <React.Fragment>
      <style>{`
        /* ── Stepper ── */
        .exam-step-num {
          width: 32px; height: 32px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700; flex-shrink: 0; transition: all 0.2s;
        }
        .exam-step-num.active { background: #6366f1; color: #fff; }
        .exam-step-num.done   { background: #059669; color: #fff; }
        .exam-step-num.pending{ background: #f3f4f6; color: #9ca3af; border: 2px solid #e5e7eb; }
        .exam-step-label { font-size: 13px; font-weight: 500; margin-left: 10px; }
        .exam-step-label.active  { color: #1a1d2e; }
        .exam-step-label.done    { color: #059669; }
        .exam-step-label.pending { color: #9ca3af; }
        .exam-step-line {
          flex: 1; height: 2px; background: #e5e7eb;
          margin: 0 12px; min-width: 30px; transition: background 0.3s;
        }
        .exam-step-line.done { background: #059669; }

        /* ── Assessment table (Step 2) ── */
        .assess-table th {
          font-size: 11px; font-weight: 600; color: #6b7280;
          text-transform: uppercase; letter-spacing: 0.5px;
          background: #f9fafb; padding: 10px 14px; white-space: nowrap;
          border-bottom: 1px solid #e5e7eb;
        }
        .assess-table td { padding: 10px 14px; vertical-align: top; border-bottom: 1px solid #f3f4f6; }
        .assess-table tbody tr.assess-sub-row td { border-bottom: 1px solid #f3f4f6; }
        .assess-table tbody tr:last-child td { border-bottom: none; }

        .row-input {
          width: 100%; padding: 7px 10px;
          border: 1px solid #d1d5db; border-radius: 6px;
          font-size: 13px; outline: none; color: #1a1d2e;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .row-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
        .row-input.is-invalid { border-color: #f46a6a; }
        .row-error { color: #f46a6a; font-size: 11px; margin-top: 4px; display: flex; align-items: center; gap: 4px; }

        .row-desc-input {
          width: 100%; padding: 6px 10px;
          border: 1px solid #e5e7eb; border-radius: 6px;
          font-size: 12px; outline: none; color: #374151;
          background: #fafafa; transition: border-color 0.15s; resize: vertical; min-height: 34px;
        }
        .row-desc-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); background: #fff; }

        .mark-cell-input {
          width: 100%; padding: 7px 10px;
          border: 1px solid #d1d5db; border-radius: 6px;
          font-size: 13px; outline: none; color: #1a1d2e;
          text-align: center !important;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .mark-cell-input::placeholder { text-align: center; color: #9ca3af; opacity: 1; }
        .mark-cell-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
        .mark-cell-input::-webkit-outer-spin-button,
        .mark-cell-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .mark-cell-input[type="number"] { -moz-appearance: textfield; }

        .action-add-btn {
          display: inline-flex; align-items: center; gap: 4px;
          background: #f0fdf4; color: #059669; border: 1px solid #a7f3d0;
          border-radius: 6px; padding: 5px 12px; cursor: pointer; font-size: 12px; font-weight: 600;
          transition: all 0.15s; white-space: nowrap;
        }
        .action-add-btn:hover { background: #dcfce7; border-color: #059669; }
        .action-add-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .action-sub-btn {
          display: inline-flex; align-items: center; gap: 4px;
          background: #eef2ff; color: #4338ca; border: 1px solid #c7d2fe;
          border-radius: 6px; padding: 5px 12px; cursor: pointer; font-size: 12px; font-weight: 600;
          transition: all 0.15s; white-space: nowrap;
        }
        .action-sub-btn:hover { background: #e0e7ff; border-color: #6366f1; }

        .action-del-btn {
          display: inline-flex; align-items: center; gap: 4px;
          background: #fee2e2; color: #dc2626; border: 1px solid #fca5a5;
          border-radius: 6px; padding: 5px 12px; cursor: pointer; font-size: 12px; font-weight: 600;
          transition: all 0.15s; white-space: nowrap;
        }
        .action-del-btn:hover { background: #fecaca; border-color: #dc2626; }
        .action-del-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        /* ── Chip-based sub-assessment panel ── */
        .sub-panel {
          background: #f5f3ff;
          border-left: 3px solid #a5b4fc;
          border-radius: 0 8px 8px 0;
          padding: 12px 16px 14px;
          margin-top: 2px;
        }
        .sub-panel-title {
          font-size: 11px; font-weight: 700; color: #4338ca;
          text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px;
        }
        .sub-input-row {
          display: flex; align-items: center; gap: 8px; margin-bottom: 10px;
        }
        .sub-name-input {
          flex: 1; max-width: 300px;
          padding: 6px 10px;
          border: 1px solid #c7d2fe; border-radius: 6px;
          font-size: 13px; outline: none; color: #1a1d2e; background: #fff;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .sub-name-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
        .sub-name-input:disabled { background: #f3f4f6; color: #9ca3af; cursor: not-allowed; border-color: #e5e7eb; }
        .sub-name-input::placeholder { color: #9ca3af; }

        .sub-add-btn {
          display: inline-flex; align-items: center; gap: 4px;
          background: #6366f1; color: #fff;
          border: none; border-radius: 6px;
          padding: 6px 14px; cursor: pointer; font-size: 12px; font-weight: 600;
          transition: background 0.15s; white-space: nowrap; flex-shrink: 0;
        }
        .sub-add-btn:hover:not(:disabled) { background: #4338ca; }
        .sub-add-btn:disabled { background: #c7d2fe; cursor: not-allowed; }

        .sub-chips-wrap {
          display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
        }
        .sub-chip {
          display: inline-flex; align-items: center; gap: 5px;
          background: #fff; border: 1.5px solid #a5b4fc;
          border-radius: 20px; padding: 4px 10px 4px 12px;
          font-size: 12px; font-weight: 600; color: #3730a3;
          transition: border-color 0.15s, background 0.15s;
          max-width: 200px;
        }
        .sub-chip:hover { border-color: #6366f1; background: #eef2ff; }
        .sub-chip-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .sub-chip-remove {
          display: inline-flex; align-items: center; justify-content: center;
          width: 16px; height: 16px; border-radius: 50%;
          background: #e0e7ff; border: none; cursor: pointer; flex-shrink: 0;
          color: #4338ca; font-size: 11px; font-weight: 700; line-height: 1;
          padding: 0; transition: background 0.15s, color 0.15s;
        }
        .sub-chip-remove:hover { background: #dc2626; color: #fff; }
        .sub-limit-note { font-size: 11px; color: #9ca3af; font-style: italic; margin-top: 6px; }

        /* ── Assessment Cards (Step 3) ── */
        .assessment-cards-grid { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 4px; }
        .assessment-card {
          position: relative; cursor: pointer;
          border: 2px solid #e5e7eb; border-radius: 10px;
          padding: 10px 16px 10px 12px;
          background: #fff; min-width: 130px; max-width: 220px;
          flex: 1 1 130px;
          transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
          user-select: none;
          display: flex; align-items: center; gap: 8px;
        }
        .assessment-card:hover { border-color: #a5b4fc; background: #f5f3ff; }
        .assessment-card.selected {
          border-color: #6366f1; background: #eef2ff;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.13);
        }
        .assessment-card-icon {
          width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          border: 2px solid #d1d5db; background: #fff; transition: all 0.18s;
        }
        .assessment-card.selected .assessment-card-icon { border-color: #6366f1; background: #6366f1; }
        .assessment-card-parent-label { font-size: 10px; font-weight: 500; color: #9ca3af; display: block; }
        .assessment-card-label {
          font-size: 13px; font-weight: 600; color: #374151; line-height: 1.3; transition: color 0.18s;
        }
        .assessment-card.selected .assessment-card-label { color: #3730a3; }

        /* ── Mapping card (Step 3) ── */
        .mapping-card-header { background: #faf5ff; border-bottom: 1px solid #f3f4f6; }
        .mapping-card-title {
          font-size: 14px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.5px; color: #4c1d95;
        }
        .mapping-card-parent { font-size: 11px; font-weight: 500; color: #a78bfa; margin-right: 4px; }
        .class-chip-empty { font-size: 12px; color: #9ca3af; padding: 7px 0; }

        /* ── Class / Category multi-select dropdown (shared styles) ── */
        .class-multiselect { position: relative; }
        .class-multiselect-wrap { position: relative; }
        .class-multiselect-trigger {
          width: 100%; display: flex; align-items: center; justify-content: space-between;
          gap: 8px; padding: 7px 10px;
          border: 1px solid #d1d5db; border-radius: 6px;
          background: #fff; cursor: pointer; text-align: left;
          font-size: 13px; color: #1a1d2e;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .class-multiselect-trigger:hover { border-color: #a5b4fc; }
        .class-multiselect-trigger.open { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
        .class-multiselect-trigger-text { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .class-multiselect-trigger-text.placeholder { color: #9ca3af; }
        .class-multiselect-chevron { flex-shrink: 0; transition: transform 0.15s; }
        .class-multiselect-trigger.open .class-multiselect-chevron { transform: rotate(180deg); }
        .class-multiselect-panel {
          position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 20;
          background: #fff; border: 1px solid #e5e7eb; border-radius: 8px;
          box-shadow: 0 8px 24px rgba(15,23,42,0.12);
          max-height: 240px; overflow-y: auto; padding: 4px;
        }
        .class-multiselect-option {
          display: flex; align-items: center; gap: 8px;
          padding: 7px 8px; border-radius: 6px; cursor: pointer;
          font-size: 13px; color: #374151;
        }
        .class-multiselect-option:hover { background: #f5f3ff; }
        .class-multiselect-option-all { font-weight: 600; color: #4338ca; }
        .class-multiselect-option-label { line-height: 1.2; }
        .class-multiselect-divider { height: 1px; background: #f3f4f6; margin: 4px 2px; }

        /* ── Subject mapping table ── */
        .subject-mapping-table th {
          font-size: 11px; font-weight: 600; color: #6b7280;
          text-transform: uppercase; letter-spacing: 0.5px;
          background: #f9fafb; padding: 10px 14px; white-space: nowrap;
          border-bottom: 1px solid #e5e7eb;
        }
        .subject-mapping-table td { padding: 9px 14px; vertical-align: middle; border-bottom: 1px solid #f3f4f6; }
        .subject-mapping-table tbody tr:last-child td { border-bottom: none; }
        .subject-mapping-table tbody tr.row-unchecked { background: #fafafa; opacity: 0.65; }
        .subject-mapping-table tbody tr.row-unchecked:hover { opacity: 0.8; }
        .subject-mapping-table tbody tr.subject-header-row { background: #f3f4f6; }
        .subject-mapping-table tbody tr.subject-header-row td { padding: 7px 14px; }
        .topic-row-label { display: inline-flex; align-items: center; }

        /* ── Custom checkbox ── */
        .subj-checkbox-wrap { display: flex; align-items: center; justify-content: center; cursor: pointer; }
        .subj-checkbox {
          width: 17px; height: 17px; border-radius: 4px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s; border: 2px solid #d1d5db; background: #fff; cursor: pointer;
        }
        .subj-checkbox.checked     { border-color: #6366f1; background: #6366f1; }
        .subj-checkbox.indeterminate { border-color: #6366f1; background: #6366f1; }

        .mark-input {
          width: 100%; padding: 6px 10px;
          border: 1px solid #d1d5db; border-radius: 6px;
          font-size: 13px; outline: none; color: #1a1d2e;
          text-align: center !important;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .mark-input::placeholder { text-align: center; color: #9ca3af; opacity: 1; }
        .mark-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
        .mark-input:disabled { background: #f3f4f6; color: #9ca3af; cursor: not-allowed; border-color: #e5e7eb; }
        .mark-input::-webkit-outer-spin-button,
        .mark-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .mark-input[type="number"] { -moz-appearance: textfield; }

        /* ── Category loading spinner row ── */
        .category-loading-more {
          text-align: center; padding: 8px 4px;
          font-size: 12px; color: #9ca3af;
          display: flex; align-items: center; justify-content: center; gap: 6px;
        }
        .category-all-loaded {
          text-align: center; padding: 6px 4px;
          font-size: 11px; color: #d1d5db;
        }

        /* Badges */
        .subj-selected-badge {
          display: inline-flex; align-items: center;
          background: #ede9fe; color: #4c1d95;
          border-radius: 20px; padding: 2px 10px;
          font-size: 11px; font-weight: 700; margin-left: 10px;
        }
        .eval-type-badge {
          display: inline-flex; align-items: center;
          border-radius: 20px; padding: 2px 10px;
          font-size: 11px; font-weight: 700; margin-left: 8px;
        }
        .eval-type-badge.grade { background: #fef3c7; color: #92400e; }
        .eval-type-badge.marks { background: #d1fae5; color: #065f46; }
      `}</style>

      <div className="page-content">
        <div className="container-fluid">
          <Container fluid={true}>
            <Breadcrumb title="Examination" breadcrumbItem="Exam Create" />

            <p className="text-muted mb-4" style={{ fontSize: "13px" }}>
              {step === 2
                ? "Add Assessments"
                : step === 3
                ? "Map subjects to assessment & class"
                : ""}
            </p>

            <Stepper />

            <div className="card">
              <div className="card-body">
                <Form
                  onSubmit={e => {
                    e.preventDefault()
                    validation.handleSubmit()
                  }}
                >
                  {/* ========== STEP 1 ========== */}
                  {step === 1 && (
                    <>
                      <Row className="mb-4">
                        <Col lg={12}>
                          <label
                            className="form-label"
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            Exam Name *
                          </label>
                          <Input
                            id="exam_name"
                            name="exam_name"
                            type="text"
                            className="form-control"
                            placeholder="e.g. Half Yearly Examination"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.exam_name}
                            invalid={
                              !!(
                                validation.touched.exam_name &&
                                validation.errors.exam_name
                              )
                            }
                          />
                          {validation.touched.exam_name &&
                          validation.errors.exam_name ? (
                            <FormFeedback type="invalid">
                              {validation.errors.exam_name}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <Row className="mb-4">
                        <Col lg={12}>
                          <label
                            className="form-label"
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            Description *
                          </label>
                          <Input
                            id="description"
                            name="description"
                            type="textarea"
                            className="form-control"
                            placeholder="Enter exam description..."
                            rows={3}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.description}
                            invalid={
                              !!(
                                validation.touched.description &&
                                validation.errors.description
                              )
                            }
                          />
                          {validation.touched.description &&
                          validation.errors.description ? (
                            <FormFeedback type="invalid">
                              {validation.errors.description}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <div
                        className="d-flex justify-content-between mt-4"
                        style={{
                          borderTop: "1px solid #f3f4f6",
                          paddingTop: "16px",
                        }}
                      >
                        <Button color="secondary" onClick={handleCancel}>
                          Cancel
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          className="waves-effect waves-light"
                          disabled={loading}
                        >
                          {loading ? "Saving..." : "Next: Assessment →"}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* ========== STEP 2 ========== */}
                  {step === 2 && (
                    <>
                      <div className="card mb-3">
                        <div
                          className="card-header d-flex justify-content-between align-items-center"
                          style={{ borderBottom: "1px solid #f3f4f6" }}
                        >
                          <h6
                            className="mb-0"
                            style={{ fontSize: "14px", fontWeight: 600 }}
                          >
                            Assessments
                          </h6>
                          <span style={{ fontSize: "12px", color: "#6b7280" }}>
                            {assessmentRows.length}{" "}
                            {assessmentRows.length === 1 ? "row" : "rows"}
                          </span>
                        </div>
                        <div style={{ overflowX: "auto" }}>
                          <Table className="mb-0 assess-table">
                            <thead>
                              <tr>
                                <th style={{ width: "60px" }}>Sr. No.</th>
                                <th style={{ width: "24%" }}>
                                  Assessment Name *
                                </th>
                                <th>
                                  Description
                                  <span
                                    style={{
                                      marginLeft: "6px",
                                      fontSize: "10px",
                                      fontWeight: 400,
                                      color: "#9ca3af",
                                      textTransform: "none",
                                      letterSpacing: 0,
                                    }}
                                  >
                                    (optional)
                                  </span>
                                </th>
                                <th
                                  style={{
                                    width: "110px",
                                    textAlign: "center",
                                  }}
                                >
                                  Min Marks
                                </th>
                                <th
                                  style={{
                                    width: "110px",
                                    textAlign: "center",
                                  }}
                                >
                                  Max Marks
                                </th>
                                <th
                                  style={{
                                    width: "190px",
                                    textAlign: "center",
                                  }}
                                >
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {assessmentRows.map((row, index) => (
                                <React.Fragment key={row.id}>
                                  <tr>
                                    <td style={{ verticalAlign: "middle" }}>
                                      <span
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          width: "26px",
                                          height: "26px",
                                          borderRadius: "50%",
                                          background: "#f3f4f6",
                                          color: "#6b7280",
                                          fontSize: "12px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {index + 1}
                                      </span>
                                    </td>
                                    <td style={{ verticalAlign: "top" }}>
                                      <input
                                        className={`row-input ${
                                          rowErrors[row.id] ? "is-invalid" : ""
                                        }`}
                                        type="text"
                                        placeholder="Enter assessment name..."
                                        value={row.name}
                                        onChange={e =>
                                          handleRowChange(
                                            row.id,
                                            "name",
                                            e.target.value
                                          )
                                        }
                                      />
                                      {rowErrors[row.id] && (
                                        <div className="row-error">
                                          <svg
                                            width="11"
                                            height="11"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                          >
                                            <circle
                                              cx="10"
                                              cy="10"
                                              r="9"
                                              stroke="#f46a6a"
                                              strokeWidth="2"
                                            />
                                            <path
                                              d="M10 6v5"
                                              stroke="#f46a6a"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                            />
                                            <circle
                                              cx="10"
                                              cy="14.5"
                                              r="1"
                                              fill="#f46a6a"
                                            />
                                          </svg>
                                          {rowErrors[row.id]}
                                        </div>
                                      )}
                                    </td>
                                    <td style={{ verticalAlign: "top" }}>
                                      <textarea
                                        className="row-desc-input"
                                        rows={1}
                                        placeholder="Enter description..."
                                        value={row.description}
                                        onChange={e =>
                                          handleRowChange(
                                            row.id,
                                            "description",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </td>
                                    <td style={{ verticalAlign: "top" }}>
                                      <input
                                        className="mark-cell-input"
                                        type="number"
                                        min={0}
                                        placeholder="0"
                                        value={row.minMarks}
                                        onChange={e =>
                                          handleRowChange(
                                            row.id,
                                            "minMarks",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </td>
                                    <td style={{ verticalAlign: "top" }}>
                                      <input
                                        className="mark-cell-input"
                                        type="number"
                                        min={0}
                                        placeholder="0"
                                        value={row.maxMarks}
                                        onChange={e =>
                                          handleRowChange(
                                            row.id,
                                            "maxMarks",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </td>
                                    <td style={{ verticalAlign: "middle" }}>
                                      <div className="d-flex align-items-center justify-content-center gap-2">
                                        <button
                                          type="button"
                                          className="action-sub-btn"
                                          onClick={() =>
                                            handleToggleExpand(row.id)
                                          }
                                        >
                                          {row.expanded ? "− Sub" : "+ Sub"}
                                        </button>
                                        <button
                                          type="button"
                                          className="action-del-btn"
                                          onClick={() =>
                                            handleRemoveRow(row.id)
                                          }
                                          disabled={assessmentRows.length === 1}
                                        >
                                          🗑 Delete
                                        </button>
                                      </div>
                                    </td>
                                  </tr>

                                  {row.expanded && (
                                    <tr className="assess-sub-row">
                                      <td
                                        style={{
                                          borderBottom: "1px solid #f3f4f6",
                                          paddingTop: 0,
                                          paddingBottom: 8,
                                        }}
                                      />
                                      <td
                                        colSpan={5}
                                        style={{
                                          borderBottom: "1px solid #f3f4f6",
                                          paddingTop: 0,
                                          paddingBottom: 8,
                                        }}
                                      >
                                        <div className="sub-panel">
                                          <div className="sub-panel-title">
                                            Sub-assessments
                                            {row.name.trim() && (
                                              <span
                                                style={{
                                                  fontWeight: 400,
                                                  textTransform: "none",
                                                  color: "#6366f1",
                                                }}
                                              >
                                                {" "}
                                                under "{row.name.trim()}"
                                              </span>
                                            )}
                                          </div>
                                          <div className="sub-input-row">
                                            <input
                                              className="sub-name-input"
                                              type="text"
                                              placeholder="Sub-assessment name…"
                                              value={row.newSubName || ""}
                                              disabled={
                                                row.subAssessments.length >=
                                                MAX_SUB_ASSESSMENTS
                                              }
                                              onChange={e =>
                                                handleNewSubNameChange(
                                                  row.id,
                                                  e.target.value
                                                )
                                              }
                                              onKeyDown={e =>
                                                handleNewSubKeyDown(row.id, e)
                                              }
                                            />
                                            <button
                                              type="button"
                                              className="sub-add-btn"
                                              disabled={
                                                !(
                                                  row.newSubName || ""
                                                ).trim() ||
                                                row.subAssessments.length >=
                                                  MAX_SUB_ASSESSMENTS
                                              }
                                              onClick={() =>
                                                handleAddSubAssessment(row.id)
                                              }
                                            >
                                              + Add
                                            </button>
                                          </div>
                                          {row.subAssessments.length > 0 && (
                                            <div className="sub-chips-wrap">
                                              {row.subAssessments.map(sub => (
                                                <span
                                                  key={sub.id}
                                                  className="sub-chip"
                                                >
                                                  <span
                                                    className="sub-chip-name"
                                                    title={sub.name}
                                                  >
                                                    {sub.name}
                                                  </span>
                                                  <button
                                                    type="button"
                                                    className="sub-chip-remove"
                                                    title={`Remove "${sub.name}"`}
                                                    onClick={() =>
                                                      handleRemoveSubAssessment(
                                                        row.id,
                                                        sub.id
                                                      )
                                                    }
                                                  >
                                                    ✕
                                                  </button>
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                          {row.subAssessments.length >=
                                            MAX_SUB_ASSESSMENTS && (
                                            <div className="sub-limit-note">
                                              Maximum {MAX_SUB_ASSESSMENTS}{" "}
                                              sub-assessments reached.
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="action-add-btn mb-2"
                        onClick={handleAddRow}
                      >
                        + Add Assessment
                      </button>

                      <div
                        className="d-flex justify-content-between mt-4"
                        style={{
                          borderTop: "1px solid #f3f4f6",
                          paddingTop: "16px",
                        }}
                      >
                        <Button color="secondary" onClick={handlePrevious}>
                          Back
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          className="waves-effect waves-light"
                          disabled={loading}
                        >
                          {loading ? "Saving..." : "Next: Subject Mapping →"}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* ========== STEP 3: Subject Mapping ========== */}
                  {step === 3 && (
                    <>
                      <div className="mb-4">
                        <label
                          className="form-label"
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#374151",
                            marginBottom: "10px",
                            display: "block",
                          }}
                        >
                          Select Assessment(s) *
                        </label>
                        {assessmentCards.length === 0 ? (
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#9ca3af",
                              padding: "12px 0",
                            }}
                          >
                            No assessments added in Step 2.
                          </div>
                        ) : (
                          <div className="assessment-cards-grid">
                            {assessmentCards.map(card => {
                              const isSelected = selectedCardKeys.includes(
                                card.key
                              )
                              return (
                                <div
                                  key={card.key}
                                  className={`assessment-card${
                                    isSelected ? " selected" : ""
                                  }`}
                                  onClick={() => handleCardToggle(card.key)}
                                >
                                  <div className="assessment-card-icon">
                                    {isSelected && (
                                      <svg
                                        width="10"
                                        height="10"
                                        viewBox="0 0 10 10"
                                        fill="none"
                                      >
                                        <path
                                          d="M1.5 5L4 7.5L8.5 2.5"
                                          stroke="#fff"
                                          strokeWidth="1.8"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                  <span>
                                    {card.parentLabel && (
                                      <span className="assessment-card-parent-label">
                                        {card.parentLabel}
                                      </span>
                                    )}
                                    <span className="assessment-card-label">
                                      {card.label}
                                    </span>
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        )}
                        {selectedCardKeys.length > 0 && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6366f1",
                              marginTop: "6px",
                              fontWeight: 500,
                            }}
                          >
                            {selectedCardKeys.length} assessment
                            {selectedCardKeys.length > 1 ? "s" : ""} selected
                          </div>
                        )}
                      </div>

                      {selectedCardKeys.length === 0 ? (
                        <div
                          className="text-center py-5"
                          style={{ color: "#9ca3af", fontSize: "13px" }}
                        >
                          Please select at least one Assessment to map its
                          Category, Class and Subjects.
                        </div>
                      ) : (
                        selectedCardKeys.map(key => {
                          const card = cardsByKey[key]
                          if (!card) return null
                          const mapping = getMapping(key)
                          const category = allCategories.find(
                            c => String(c.id) === String(mapping.categoryId)
                          )
                          const classes = mapping.categoryId
                            ? getClassesForCategory(mapping.categoryId)
                            : []
                          const isGradeBased =
                            category?.evaluation_type === "Grade Based"
                          const isTopicWise =
                            category?.marking === "Subject + Topic Wise"
                          const selectableSubjects = mapping.subjects.filter(
                            s => s.rowType !== "subject-header"
                          )
                          const checkedCount = selectableSubjects.filter(
                            s => s.checked
                          ).length
                          const allChecked =
                            selectableSubjects.length > 0 &&
                            selectableSubjects.every(s => s.checked)
                          const someChecked =
                            selectableSubjects.some(s => s.checked) &&
                            !allChecked

                          return (
                            <div className="card mb-3" key={key}>
                              <div className="card-header d-flex align-items-center mapping-card-header">
                                {card.parentLabel && (
                                  <span className="mapping-card-parent">
                                    {card.parentLabel} /
                                  </span>
                                )}
                                <h6 className="mb-0 mapping-card-title">
                                  {card.label}
                                </h6>
                                {category && (
                                  <span
                                    className={`eval-type-badge ${
                                      isGradeBased ? "grade" : "marks"
                                    }`}
                                  >
                                    {category.evaluation_type}
                                  </span>
                                )}
                                {selectableSubjects.length > 0 && (
                                  <span className="subj-selected-badge ms-auto">
                                    {checkedCount} / {selectableSubjects.length}{" "}
                                    selected
                                  </span>
                                )}
                              </div>

                              <div className="card-body">
                                <div
                                  className="category-class-row mb-4"
                                  style={{
                                    display: "flex",
                                    gap: "16px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {/* ── Category custom dropdown with infinite scroll ── */}
                                  <div
                                    style={{
                                      flex: "1 1 240px",
                                      minWidth: "200px",
                                    }}
                                  >
                                    <label
                                      className="form-label"
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#374151",
                                      }}
                                    >
                                      Category *
                                    </label>

                                    {categoriesLoading ? (
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color: "#9ca3af",
                                          padding: "8px 0",
                                        }}
                                      >
                                        Loading categories...
                                      </div>
                                    ) : (
                                      <div className="class-multiselect">
                                        <button
                                          type="button"
                                          className={`class-multiselect-trigger${
                                            openCategoryDropdownKey === key
                                              ? " open"
                                              : ""
                                          }`}
                                          onClick={() =>
                                            setOpenCategoryDropdownKey(prev =>
                                              prev === key ? null : key
                                            )
                                          }
                                        >
                                          <span
                                            className={`class-multiselect-trigger-text${
                                              !mapping.categoryId
                                                ? " placeholder"
                                                : ""
                                            }`}
                                          >
                                            {mapping.categoryId
                                              ? allCategories.find(
                                                  c =>
                                                    String(c.id) ===
                                                    String(mapping.categoryId)
                                                )?.category_name ??
                                                "Select Category"
                                              : "Select Category"}
                                          </span>
                                          <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                            className="class-multiselect-chevron"
                                          >
                                            <path
                                              d="M2.5 4.5L6 8L9.5 4.5"
                                              stroke="#6b7280"
                                              strokeWidth="1.6"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </button>

                                        {openCategoryDropdownKey === key && (
                                          <div
                                            className="class-multiselect-panel"
                                            onScroll={handleCategoryPanelScroll}
                                          >
                                            <div
                                              className="class-multiselect-option"
                                              onClick={() => {
                                                handleCategoryChange(key, "")
                                                setOpenCategoryDropdownKey(null)
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "13px",
                                                  color: "#9ca3af",
                                                  fontStyle: "italic",
                                                }}
                                              >
                                                Select Category
                                              </span>
                                            </div>
                                            <div className="class-multiselect-divider" />

                                            {allCategories.map(c => {
                                              const isSelected =
                                                String(mapping.categoryId) ===
                                                String(c.id)
                                              return (
                                                <div
                                                  key={c.id}
                                                  className="class-multiselect-option"
                                                  style={{
                                                    background: isSelected
                                                      ? "#eef2ff"
                                                      : undefined,
                                                    fontWeight: isSelected
                                                      ? 600
                                                      : 400,
                                                    color: isSelected
                                                      ? "#3730a3"
                                                      : "#374151",
                                                  }}
                                                  onClick={() => {
                                                    handleCategoryChange(
                                                      key,
                                                      c.id
                                                    )
                                                    setOpenCategoryDropdownKey(
                                                      null
                                                    )
                                                  }}
                                                >
                                                  <span className="class-multiselect-option-label">
                                                    {c.category_name}
                                                  </span>
                                                </div>
                                              )
                                            })}

                                            {categoriesLoadingMore && (
                                              <div className="category-loading-more">
                                                <svg
                                                  width="14"
                                                  height="14"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  style={{
                                                    animation:
                                                      "spin 1s linear infinite",
                                                  }}
                                                >
                                                  <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="#d1d5db"
                                                    strokeWidth="3"
                                                  />
                                                  <path
                                                    d="M12 2a10 10 0 0 1 10 10"
                                                    stroke="#6366f1"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                  />
                                                </svg>
                                                Loading more...
                                              </div>
                                            )}

                                            {!categoriesLoadingMore &&
                                              categoriesPage >=
                                                categoriesTotalPages &&
                                              allCategories.length > 0 && (
                                                <div className="category-all-loaded">
                                                  ✓ All categories loaded
                                                </div>
                                              )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* ── Class multi-select dropdown ── */}
                                  <div
                                    className="class-multiselect"
                                    style={{
                                      flex: "1 1 240px",
                                      minWidth: "200px",
                                    }}
                                  >
                                    <label
                                      className="form-label"
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#374151",
                                      }}
                                    >
                                      Class *
                                    </label>
                                    {!mapping.categoryId ? (
                                      <div className="class-chip-empty">
                                        Select a category first
                                      </div>
                                    ) : classes.length === 0 ? (
                                      <div className="class-chip-empty">
                                        No classes found for this category
                                      </div>
                                    ) : (
                                      <div className="class-multiselect-wrap">
                                        <button
                                          type="button"
                                          className={`class-multiselect-trigger${
                                            openClassDropdownKey === key
                                              ? " open"
                                              : ""
                                          }`}
                                          onClick={() =>
                                            setOpenClassDropdownKey(prev =>
                                              prev === key ? null : key
                                            )
                                          }
                                        >
                                          <span
                                            className={`class-multiselect-trigger-text${
                                              mapping.classIds.length === 0
                                                ? " placeholder"
                                                : ""
                                            }`}
                                          >
                                            {mapping.classIds.length === 0
                                              ? "Select Class(es)"
                                              : classes
                                                  .filter(c =>
                                                    mapping.classIds.includes(
                                                      c.id
                                                    )
                                                  )
                                                  .map(c => c.class_name)
                                                  .join(", ")}
                                          </span>
                                          <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                            className="class-multiselect-chevron"
                                          >
                                            <path
                                              d="M2.5 4.5L6 8L9.5 4.5"
                                              stroke="#6b7280"
                                              strokeWidth="1.6"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </button>

                                        {openClassDropdownKey === key && (
                                          <div className="class-multiselect-panel">
                                            <div
                                              className="class-multiselect-option class-multiselect-option-all"
                                              onClick={() => {
                                                const allSelected =
                                                  classes.length > 0 &&
                                                  classes.every(c =>
                                                    mapping.classIds.includes(
                                                      c.id
                                                    )
                                                  )
                                                setMappingState(prev => {
                                                  const current = prev[key] || {
                                                    categoryId: "",
                                                    classIds: [],
                                                    subjects: [],
                                                  }
                                                  return {
                                                    ...prev,
                                                    [key]: {
                                                      ...current,
                                                      classIds: allSelected
                                                        ? []
                                                        : classes.map(
                                                            c => c.id
                                                          ),
                                                    },
                                                  }
                                                })
                                              }}
                                            >
                                              <span
                                                className={`subj-checkbox ${
                                                  classes.length > 0 &&
                                                  classes.every(c =>
                                                    mapping.classIds.includes(
                                                      c.id
                                                    )
                                                  )
                                                    ? "checked"
                                                    : ""
                                                }`}
                                              >
                                                {classes.length > 0 &&
                                                  classes.every(c =>
                                                    mapping.classIds.includes(
                                                      c.id
                                                    )
                                                  ) && (
                                                    <svg
                                                      width="9"
                                                      height="9"
                                                      viewBox="0 0 10 10"
                                                      fill="none"
                                                    >
                                                      <path
                                                        d="M1.5 5L4 7.5L8.5 2.5"
                                                        stroke="#fff"
                                                        strokeWidth="1.8"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                      />
                                                    </svg>
                                                  )}
                                              </span>
                                              <span className="class-multiselect-option-label">
                                                Select All
                                              </span>
                                            </div>
                                            <div className="class-multiselect-divider" />
                                            {classes.map(cls => {
                                              const checked =
                                                mapping.classIds.includes(
                                                  cls.id
                                                )
                                              return (
                                                <div
                                                  key={cls.id}
                                                  className="class-multiselect-option"
                                                  onClick={() =>
                                                    handleClassToggle(
                                                      key,
                                                      cls.id
                                                    )
                                                  }
                                                >
                                                  <span
                                                    className={`subj-checkbox ${
                                                      checked ? "checked" : ""
                                                    }`}
                                                  >
                                                    {checked && (
                                                      <svg
                                                        width="9"
                                                        height="9"
                                                        viewBox="0 0 10 10"
                                                        fill="none"
                                                      >
                                                        <path
                                                          d="M1.5 5L4 7.5L8.5 2.5"
                                                          stroke="#fff"
                                                          strokeWidth="1.8"
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                        />
                                                      </svg>
                                                    )}
                                                  </span>
                                                  <span className="class-multiselect-option-label">
                                                    {cls.class_name}
                                                  </span>
                                                </div>
                                              )
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* ── Subject table ── */}
                                {mapping.categoryId &&
                                  mapping.classIds.length > 0 &&
                                  (mapping.subjects.length === 0 ? (
                                    <div
                                      className="text-center py-4"
                                      style={{
                                        color: "#9ca3af",
                                        fontSize: "13px",
                                      }}
                                    >
                                      No subjects found for this category.
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        overflowX: "auto",
                                        border: "1px solid #f3f4f6",
                                        borderRadius: "8px",
                                      }}
                                    >
                                      <Table className="mb-0 subject-mapping-table">
                                        <thead>
                                          <tr>
                                            <th
                                              style={{
                                                width: "52px",
                                                textAlign: "center",
                                              }}
                                            >
                                              <div
                                                className="subj-checkbox-wrap"
                                                onClick={() =>
                                                  handleSelectAllSubjects(key)
                                                }
                                                title={
                                                  allChecked
                                                    ? "Deselect all"
                                                    : "Select all"
                                                }
                                              >
                                                <div
                                                  className={`subj-checkbox ${
                                                    allChecked
                                                      ? "checked"
                                                      : someChecked
                                                      ? "indeterminate"
                                                      : ""
                                                  }`}
                                                >
                                                  {allChecked && (
                                                    <svg
                                                      width="9"
                                                      height="9"
                                                      viewBox="0 0 10 10"
                                                      fill="none"
                                                    >
                                                      <path
                                                        d="M1.5 5L4 7.5L8.5 2.5"
                                                        stroke="#fff"
                                                        strokeWidth="1.8"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                      />
                                                    </svg>
                                                  )}
                                                  {someChecked &&
                                                    !allChecked && (
                                                      <svg
                                                        width="9"
                                                        height="9"
                                                        viewBox="0 0 10 10"
                                                        fill="none"
                                                      >
                                                        <path
                                                          d="M2 5h6"
                                                          stroke="#fff"
                                                          strokeWidth="2"
                                                          strokeLinecap="round"
                                                        />
                                                      </svg>
                                                    )}
                                                </div>
                                              </div>
                                            </th>
                                            <th>
                                              {isTopicWise
                                                ? "Subject / Topic"
                                                : "Subject"}
                                            </th>
                                            {!isGradeBased && (
                                              <th
                                                style={{
                                                  width: "130px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                Min Marks
                                              </th>
                                            )}
                                            {!isGradeBased && (
                                              <th
                                                style={{
                                                  width: "130px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                Max Marks
                                              </th>
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {mapping.subjects.map((s, si) => {
                                            if (
                                              s.rowType === "subject-header"
                                            ) {
                                              return (
                                                <tr
                                                  key={`header-${s.subject_code}-${si}`}
                                                  className="subject-header-row"
                                                >
                                                  <td></td>
                                                  <td
                                                    colSpan={
                                                      isGradeBased ? 1 : 3
                                                    }
                                                    style={{
                                                      fontSize: "13px",
                                                      fontWeight: 700,
                                                      color: "#374151",
                                                    }}
                                                  >
                                                    {s.subject_name}
                                                  </td>
                                                </tr>
                                              )
                                            }
                                            return (
                                              <tr
                                                key={`row-${s.subject_code}-${
                                                  s.topic_id ?? ""
                                                }-${si}`}
                                                className={
                                                  s.checked
                                                    ? ""
                                                    : "row-unchecked"
                                                }
                                              >
                                                <td
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  <div
                                                    className="subj-checkbox-wrap"
                                                    onClick={() =>
                                                      handleSubjectToggle(
                                                        key,
                                                        si
                                                      )
                                                    }
                                                  >
                                                    <div
                                                      className={`subj-checkbox ${
                                                        s.checked
                                                          ? "checked"
                                                          : ""
                                                      }`}
                                                    >
                                                      {s.checked && (
                                                        <svg
                                                          width="9"
                                                          height="9"
                                                          viewBox="0 0 10 10"
                                                          fill="none"
                                                        >
                                                          <path
                                                            d="M1.5 5L4 7.5L8.5 2.5"
                                                            stroke="#fff"
                                                            strokeWidth="1.8"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                          />
                                                        </svg>
                                                      )}
                                                    </div>
                                                  </div>
                                                </td>
                                                <td
                                                  style={{
                                                    fontSize: "13px",
                                                    fontWeight: s.checked
                                                      ? 500
                                                      : 400,
                                                    color: s.checked
                                                      ? "#1a1d2e"
                                                      : "#9ca3af",
                                                  }}
                                                >
                                                  {s.rowType === "topic" ? (
                                                    <span className="topic-row-label">
                                                      <span
                                                        style={{
                                                          color: "#9ca3af",
                                                          marginRight: "6px",
                                                        }}
                                                      >
                                                        ↳
                                                      </span>
                                                      {s.topic_name}
                                                    </span>
                                                  ) : (
                                                    s.subject_name
                                                  )}
                                                </td>
                                                {!isGradeBased && (
                                                  <td
                                                    style={{
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    <input
                                                      type="number"
                                                      className="mark-input"
                                                      value={s.min_mark ?? ""}
                                                      onChange={e =>
                                                        handleMarkChange(
                                                          key,
                                                          si,
                                                          "min_mark",
                                                          e.target.value
                                                        )
                                                      }
                                                      min={0}
                                                      placeholder="0"
                                                      disabled={!s.checked}
                                                    />
                                                  </td>
                                                )}
                                                {!isGradeBased && (
                                                  <td
                                                    style={{
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    <input
                                                      type="number"
                                                      className="mark-input"
                                                      value={s.max_mark ?? ""}
                                                      onChange={e =>
                                                        handleMarkChange(
                                                          key,
                                                          si,
                                                          "max_mark",
                                                          e.target.value
                                                        )
                                                      }
                                                      min={0}
                                                      placeholder="0"
                                                      disabled={!s.checked}
                                                    />
                                                  </td>
                                                )}
                                              </tr>
                                            )
                                          })}
                                        </tbody>
                                      </Table>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )
                        })
                      )}

                      <div
                        className="d-flex justify-content-between mt-4"
                        style={{
                          borderTop: "1px solid #f3f4f6",
                          paddingTop: "16px",
                        }}
                      >
                        <Button color="secondary" onClick={handlePrevious}>
                          Back
                        </Button>
                        <Button
                          color="success"
                          type="submit"
                          className="waves-effect waves-light"
                          disabled={loading}
                        >
                          {loading ? "Creating..." : "Save & Create Exam"}
                        </Button>
                      </div>
                    </>
                  )}
                </Form>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </React.Fragment>
  )
}

export default ExamCreate
